import {
  CardStatusEnum,
  MyProjectsProject,
  ProTeamCard,
  RepTeamCard,
} from '@platform/client/schemas';
import { Card, CardBadge, PillBadgeColors } from '@platform/components';
import { ProjectType } from '.';
import dataTestids from '../../constants/test-ids';
import { navigate } from 'wouter/use-browser-location';

export const ProUserTeamCard = ({ cardData }: { cardData: ProTeamCard }) => {
  const { project, avatar, organization } = cardData;
  if (!project.startDate || !project.endDate) {
    return null;
  }

  const completed =
    cardData.cardStatus === CardStatusEnum.project_complete ||
    cardData.cardStatus === CardStatusEnum.project_survey_pending;

  const { headerText, headerColor } = getHeaderTextAndColor(
    cardData.cardStatus,
    cardData.cardStatusChangedAt,
  );

  const [statusBadgeText, statusBadgeColor] = getStatusBadgeTextAndColor(cardData.cardStatus);

  const badges: CardBadge[] = [
    {
      text: project.roleType ? ProjectType[project.roleType] : ProjectType.consultant,
      color: 'secondary-brand-25',
    },
    {
      text: statusBadgeText,
      color: statusBadgeColor,
    },
  ];

  const headerContent = <div className='font-medium'>{headerText}</div>;

  const [footerText, _footerColor] = getFooterText(completed, cardData);

  return (
    <Card
      headerContent={headerContent}
      headerColor={headerColor}
      avatarSize='md'
      avatarHref={avatar || ''}
      content={project.name}
      contentTextHref={`/platform/directory/projects/${project.id}`}
      contentSubText={organization.name}
      contentSubtextHref={`/platform/directory/organizations/${organization.id}`}
      badges={badges}
      variant='large'
      buttonText={'Project Hub'}
      buttonVariant={'primary'}
      buttonOnClick={() => navigate(`/platform/team-project-hub/${cardData.project.id}`)}
      footerText={footerText}
      dataTestId={`${dataTestids.MY_PROJECTS.PROJECT_CARD.CONTAINER}-${project.id}`}
    />
  );
};

export const RepUserTeamCard = ({
  project,
  cardData,
}: {
  project: MyProjectsProject;
  cardData: RepTeamCard;
}) => {
  // assignments should not exist without an end date
  if (!project.startDate || !project.endDate) {
    return;
  }

  const { headerText, headerColor } = getHeaderTextAndColor(
    cardData.cardStatus,
    cardData.cardStatusChangedAt,
  );

  const [statusBadgeText, statusBadgeColor] = getStatusBadgeTextAndColor(cardData.cardStatus);

  const introductionSubtext = getIntroductionSubtext(
    cardData.cardStatus,
    new Date(cardData.cardStatusChangedAt),
  );
  const badges: CardBadge[] = [
    {
      text: statusBadgeText,
      color: statusBadgeColor,
    },
  ];

  const headerContent = <div className='font-medium'>{headerText}</div>;

  return (
    <Card
      headerContent={headerContent}
      headerColor={headerColor}
      buttonText={'Project Hub'}
      buttonVariant={'primary'}
      buttonOnClick={() => navigate(`/platform/team-project-hub/${cardData.project.id}`)}
      content={'Experteer Team'}
      avatarSize='md'
      avatarHref={''}
      contentSubText={introductionSubtext}
      badges={badges}
      variant='small'
      dataTestId={`${dataTestids.MY_PROJECTS.PROJECT_CARD.CONTAINER}-${project.id}`}
    />
  );
};

const getHeaderTextAndColor = (
  cardStatus: CardStatusEnum,
  cardStatusChangedAt: string,
): { headerColor: string; headerText: React.ReactNode } => {
  const cardStatusChangedStr = new Date(cardStatusChangedAt).toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  switch (cardStatus) {
    case CardStatusEnum.project_prep_pending:
      return {
        headerColor: 'warning-50',
        headerText: 'Congrats on matching! See your Project Hub for next steps',
      };
    case CardStatusEnum.project_work_pending:
      return {
        headerColor: 'warning-50',
        headerText: 'Your project is in progress! See your Project Hub for next steps',
      };
    case CardStatusEnum.project_survey_pending:
      return {
        headerColor: 'success-25',
        headerText:
          "You're at the finish line! Complete your wrap-up — see your Project Hub for next steps.",
      };
    case CardStatusEnum.project_complete:
      return {
        headerColor: 'success-25',
        headerText: `This project was completed on ${cardStatusChangedStr}`,
      };
    default:
      throw new Error(`Unhandled card status: ${cardStatus}`);
  }
};

const getStatusBadgeTextAndColor = (cardStatus: CardStatusEnum): [string, PillBadgeColors] => {
  if (
    cardStatus === CardStatusEnum.project_complete ||
    cardStatus === CardStatusEnum.project_survey_pending
  ) {
    return ['Completed', 'success-100'];
  } else {
    return ['Matched', 'success-25'];
  }
};
const getIntroductionSubtext = (cardStatus: CardStatusEnum, statusChangedAt: Date) => {
  const verb =
    (
      cardStatus === CardStatusEnum.project_survey_pending ||
      cardStatus === CardStatusEnum.project_complete
    ) ?
      'Completed'
    : 'Matched';
  return `${verb} ${statusChangedAt.toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

const getFooterText = (completed: boolean, cardData: RepTeamCard | ProTeamCard) => {
  if (completed) {
    return [
      `Completed ${new Date(cardData.cardStatusChangedAt).toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`,
      'success-25',
    ];
  } else {
    // TODO: I think this is a bug; this UI shouldn't even render if we don't have a teamAssignment
    const matchDate = new Date(cardData.cardStatusChangedAt);
    return [
      `Matched ${matchDate.toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`,
      'warning-50',
    ];
  }
};
